import { monkey } from 'baobab';

import typeCheck from 'common/util/typeCheck';

import {
  SR_API_ACTION_GET_LISTING_LIST,
} from 'api/actionTypes';

export const LISTINGS_DATA_STATE_MANGER_KEY = 'listingsDataManager';
export const LISTINGS_DATA_MANAGER_VIEW_ID_UNITS = 'LISTINGS_DATA_MANAGER_VIEW_ID_UNITS';
export const LISTINGS_DATA_MANAGER_VIEW_ID_APARTMENT_LISTINGS = 'LISTINGS_DATA_MANAGER_VIEW_ID_APARTMENT_LISTINGS'; // eslint-disable-line max-len

/**
 * Creates the path to access the view state in the baobab state.
 * @param {string} viewId View ID.
 */
export function makeListingsStateManagerViewPath(viewId) {
  return [LISTINGS_DATA_STATE_MANGER_KEY, 'views', viewId];
}

/**
 * Checks that the view state exits by view ID.
 * @param {Object} options.state  Baobab state object.
 * @param {string} options.viewId View ID.
 */
function makeSureListingStateViewWasConfigured({ state }, { viewId }) {
  if (!state.exists(makeListingsStateManagerViewPath(viewId))) {
    const error = new Error(`ListingsStateManager view ID "${viewId}" hasn't been configured yet.`);
    error.name = 'SrListingsStateManagerViewNotConfiguredError';
    error.details = {
      viewId,
    };
    throw error;
  }
}

/**
 * Initializes the computed data for the view in the Baobab state.
 * The monkeys are removed during the SSR, we need to recreate them on the client.
 * @param {Object} options.state  Baobab state object.
 * @param {string} options.viewId View ID.
 */
export function actionInitializeListingsStateManagerViewMonkey({ state }, { viewId }) {
  makeSureListingStateViewWasConfigured({ state }, { viewId });

  const listingsViewPath = makeListingsStateManagerViewPath(viewId);
  if (!state.exists([...listingsViewPath, '$listings'])) {
    state.set([...listingsViewPath, '$listings'], monkey({
      cursors: {
        listings: [LISTINGS_DATA_STATE_MANGER_KEY, 'listings'],
        listingIds: [...listingsViewPath, 'listingIds'],
      },
      get({ listings, listingIds }) {
        const selectedItems = listings.filter((item) => listingIds.includes(item.id));
        selectedItems.sort((listingA, listingB) => {
          return listingIds.indexOf(listingA.id) - listingIds.indexOf(listingB.id);
        });
        return selectedItems;
      },
    }));

    const currentListings = state.get([LISTINGS_DATA_STATE_MANGER_KEY, 'listings']);
    state.set([LISTINGS_DATA_STATE_MANGER_KEY, 'listings'], [ ...(currentListings || []) ]);
    state.commit();
  }
}

/**
 * Initializes a view.
 * @param {Object} state  Baobab state object.
 * @param {string} options.viewId View ID.
 * @param {Obeject} options.filters Filter object.
 * @param {Array} options.sort Sort array.
 * @param {number} options.count Listing to load by request.
 * @param {string} options.action Action type.
 */
export function actionInitializeListingStateView({ state }, {
  viewId,
  filters,
  sort,
  count,
  action,
}) {
  typeCheck('viewId::NonEmptyString', viewId);
  typeCheck('filters::NonEmptyObject', filters);
  typeCheck('sort::Maybe Array', sort);
  typeCheck('count::PositiveNumber', count);
  typeCheck('action::Maybe String', action);

  const listingsViewPath = makeListingsStateManagerViewPath(viewId);
  if (!state.exists(listingsViewPath)) {
    state.set(listingsViewPath, {
      filters,
      sort,
      count,
      action,
      page: 0,
      total: 0,
      listingIds: [],
      isFullyLoaded: false,
    });
  }

  actionInitializeListingsStateManagerViewMonkey({ state }, { viewId });
}

/**
 * Adds listings to the state.
 * @param {Object} state  Baobab state object.
 * @param {Array} options.listings Array of listings.
 */
export function actionAddListingsToStateManager({ state }, { listings }) {
  typeCheck('listings::Array', listings);

  const listingsById = listings.reduce((acc, listing) => ({
    ...acc,
    [listing.id]: listing,
  }), {});

  // Update existing listings.
  const updatedListingIds = [];
  state.get([LISTINGS_DATA_STATE_MANGER_KEY, 'listings']).forEach((currItem) => {
    const listing = listingsById[currItem.id];
    if (listing) {
      state.set([LISTINGS_DATA_STATE_MANGER_KEY, 'listings', { id: listing.id }], listing);
      updatedListingIds.push(listing.id);
    }
  });

  // Add new listings.
  const listingsToAdd = listings.filter(({ id }) => !updatedListingIds.includes(id));
  state.concat([LISTINGS_DATA_STATE_MANGER_KEY, 'listings'], listingsToAdd);
}

/**
 * Loads the next page of a view.
 *
 * @param {Object} options.state Baobab state object.
 * @param {Object} options.apiClient API client.
 * @param {string} options.viewId View ID.
 * @param {Function} options.onSuccess  Function to run after the listings are loaded.
 * @return {undefined}
 */
export async function actionListingsStateViewLoadNextPage({ state, apiClient }, {
  viewId,
  onSuccess,
}) {
  typeCheck('viewId::NonEmptyString', viewId);
  typeCheck('onSuccess::Maybe Function', onSuccess);

  makeSureListingStateViewWasConfigured({ state }, { viewId });

  const listingsViewPath = makeListingsStateManagerViewPath(viewId);
  const listingsViewConfiguration = state.get(listingsViewPath);

  await actionDataTableLoadItems({ state, apiClient }, {
    viewId,
    ...listingsViewConfiguration,
  });
}

export async function actionDataTableLoadItems({ state, apiClient }, {
  viewId,
  page,
  offset,
  count,
  filters,
  sort,
  action,
  onSuccess,
}) {
  typeCheck('viewId::Maybe NonEmptyString', viewId);
  typeCheck('page::Maybe Number', page);
  typeCheck('offset::Maybe Number', offset);
  typeCheck('count::PositiveNumber', count);
  typeCheck('filters::NonEmptyObject', filters);
  typeCheck('sort::Maybe Array', sort);
  typeCheck('action::Maybe String', action);
  typeCheck('onSuccess::Maybe Function', onSuccess);

  await apiClient.postWithState({
    action: action || SR_API_ACTION_GET_LISTING_LIST,
    state: state,
    queryId: viewId,
    payload: {
      page,
      offset,
      count,
      filters,
      sort,
    },
    onSuccess({ data, total }) {
      actionAddListingsToStateManager({ state }, { listings: data });

      const listingsViewPath = makeListingsStateManagerViewPath(viewId);
      state.concat([...listingsViewPath, 'listingIds'], data.map(({ id }) => id));
      state.set([...listingsViewPath, 'total'], total);
      state.set([...listingsViewPath, 'isFullyLoaded'], data.length < count);
      state.set([...listingsViewPath, 'page'], page + 1);

      if (onSuccess) {
        onSuccess({ data, total });
      }
    },
  });
}
